import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { Photo, Profile, UserActivity } from '../models/profile';
import { agent } from '../api/agent';
import { store } from './store';

class ProfileStore {
  profile: Profile | null = null;
  loadingProfile = false;
  uploading = false;
  loading = false;
  loadingFollowings = false;
  followings: Profile[] = [];
  activeTab = 0;
  userActivities: UserActivity[] = [];
  loadingActivities = false;

  constructor() {
    makeAutoObservable(this);
    reaction(
      () => this.activeTab,
      (activeTab) => {
        if (activeTab === 3 || activeTab === 4) {
          const predicate = activeTab === 3 ? 'followers' : 'following';
          this.loadFollowings(predicate);
        } else {
          this.followings = [];
        }
      },
    );
  }

  setActiveTab = (activeTab: number) => {
    this.activeTab = activeTab;
  };

  get isCurrentUser() {
    if (store.userStore.user && this.profile) {
      return store.userStore.user.username === this.profile.username;
    }
    return false;
  }

  loadProfile = async (username: string) => {
    this.loadingProfile = true;

    try {
      const profile = await agent.Profiles.get(username);
      runInAction(() => {
        this.profile = profile;
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.loadingProfile = false;
      });
    }
  };

  uploadPhoto = async (file: Blob) => {
    this.uploading = true;
    try {
      const response = await agent.Profiles.uploadPhoto(file);
      const photo = response.data;
      runInAction(() => {
        if (this.profile) {
          this.profile.photos?.push(photo);

          if (photo.isMain && store.userStore.user) {
            store.userStore.setImage(photo.url);
            this.profile.image = photo.url;
          }
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.uploading = false;
      });
    }
  };

  setMainPhoto = async (photo: Photo) => {
    this.loading = true;
    try {
      await agent.Profiles.setMainPhoto(photo.id);
      store.userStore.setImage(photo.url);
      runInAction(() => {
        if (this.profile && this.profile.photos) {
          const currentMain = this.profile.photos.find((p) => p.isMain);
          if (currentMain) {
            currentMain.isMain = false;
          }

          const nextMain = this.profile.photos.find((p) => p.id === photo.id);
          if (nextMain) {
            nextMain.isMain = true;
          }

          this.profile.image = photo.url;
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  deletePhoto = async (photo: Photo) => {
    this.loading = true;
    try {
      await agent.Profiles.deletePhoto(photo.id);
      runInAction(() => {
        if (this.profile && this.profile.photos) {
          this.profile.photos = this.profile.photos.filter(
            (p) => p.id !== photo.id,
          );
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  updateProfile = async (profile: Partial<Profile>) => {
    try {
      if (store.userStore.user) {
        const username = store.userStore.user.username;
        await agent.Profiles.edit(username, profile);
        runInAction(() => {
          if (this.profile) {
            this.profile = { ...this.profile, ...profile };
            if (store.userStore.user)
              store.userStore.user.displayName = this.profile.displayName;
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  updateFollowing = async (username: string, followingFlag: boolean) => {
    this.loading = true;
    try {
      await agent.Profiles.updateFollowing(username);
      store.activityStore.updateAttendeeFollowing(username);
      runInAction(() => {
        if (
          this.profile &&
          this.profile.username !== store.userStore.user?.username &&
          this.profile.username === username
        ) {
          followingFlag
            ? this.profile.followersCount++
            : this.profile.followersCount--;

          this.profile.following = !this.profile.following;
        }

        if (
          this.profile &&
          this.profile.username === store.userStore.user?.username
        ) {
          followingFlag
            ? this.profile.followingCount++
            : this.profile.followingCount--;
        }

        this.followings.forEach((profile) => {
          if (profile.username === username) {
            profile.following
              ? profile.followersCount--
              : profile.followersCount++;

            profile.following = !profile.following;
          }
        });
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  loadFollowings = async (predicate: string) => {
    if (!this.profile) throw new Error('Profile is not defined');

    this.loadingFollowings = true;
    try {
      const followings = await agent.Profiles.listFollowing(
        this.profile.username,
        predicate,
      );

      runInAction(() => {
        this.followings = followings;
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.loadingFollowings = false;
      });
    }
  };

  loadUserActivities = async (username: string, predicate = 'future') => {
    this.loadingActivities = true;
    try {
      const activities = await agent.Profiles.listUserActivities(
        username,
        predicate,
      );
      runInAction(() => {
        this.userActivities = activities;
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.loadingActivities = false;
      });
    }
  };
}

export { ProfileStore };
